import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, Lock, Mail, User, Sparkles, ArrowRight, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const AuthPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({
    username: '',
    password: '',
    email: '',
    first_name: '',
    last_name: '',
    invitation_code: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', loginData);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Welcome back!', {
        description: `Logged in as ${response.data.user.first_name} ${response.data.user.last_name}`,
      });
      
      navigate('/');
    } catch (error) {
      toast.error('Login Failed', {
        description: error.response?.data?.detail || 'Invalid username or password',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/register', registerData);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Account Created!', {
        description: 'Welcome to Williams Diversified LLC',
      });
      
      if (!response.data.user.onboarding_completed) {
        if (response.data.user.role === 'employee') {
          navigate('/employee-onboarding');
        } else if (response.data.user.role === 'vendor') {
          navigate('/vendor-onboarding');
        } else {
          navigate('/');
        }
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error('Registration Failed', {
        description: error.response?.data?.detail || 'Could not create account',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  };

  return (
    <div className="min-h-screen bg-black plaid-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Brass Lighting Effect */}
      <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
      
      {/* Animated Gold Orbs */}
      <div className="absolute w-96 h-96 bg-gold/5 rounded-full blur-3xl top-20 -left-20 animate-pulse"></div>
      <div className="absolute w-96 h-96 bg-gold/5 rounded-full blur-3xl bottom-20 -right-20 animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12 relative z-10">
        {/* Left Side - Premium Branding */}
        <div className="hidden lg:flex flex-col justify-center items-start space-y-10 p-8">
          <div className="space-y-8">
            <img 
              src="/williams-logo-transparent.png" 
              alt="Williams Diversified LLC" 
              className="h-36 w-auto"
            />
            <div className="space-y-6">
              {/* Tagline with borders */}
              <div className="inline-block border-t-2 border-b-2 border-[#C9A961] py-3 px-6">
                <span className="text-[#C9A961] text-xs font-semibold tracking-[0.3em] uppercase animate-fadeInOut">
                  VISION. INTEGRITY. RESULTS. EXCELLENCE.
                </span>
              </div>
              
              <h1 className="text-7xl font-bold leading-tight font-serif">
                <span className="text-white">Command</span>
                <br />
                <span className="text-[#C9A961] italic">Center</span>
              </h1>
              
              {/* Gold Divider */}
              <div className="w-24 h-1 bg-[#C9A961]"></div>
              
              <p className="text-gray-300 text-xl leading-relaxed max-w-md" style={{ color: '#F5F5DC' }}>
                Manage construction projects, teams, and operations with elegance and efficiency.
              </p>
            </div>
          </div>

          {/* Feature Highlights - Premium Style */}
          <div className="space-y-4 w-full">
            {[
              { icon: Shield, text: 'Secure & Role-Based Access' },
              { icon: Sparkles, text: 'AI-Powered Assistance' },
              { icon: ArrowRight, text: 'Real-Time Project Tracking' },
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-5 rounded-lg border border-gold/20 hover:border-gold bg-black/50 hover:bg-gold/10 transition-all duration-300 group cursor-pointer"
                >
                  <div className="p-3 rounded-lg bg-gold/10 group-hover:bg-gold/20 transition-colors">
                    <Icon className="h-6 w-6 text-gold" />
                  </div>
                  <span className="text-gray-300 text-lg group-hover:text-gold transition-colors">{feature.text}</span>
                </div>
              );
            })}
          </div>

          <div className="text-sm text-gray-500 pt-4">
            © 2025 Williams Diversified LLC. All rights reserved.
          </div>
        </div>

        {/* Right Side - Auth Forms */}
        <div className="flex items-center justify-center p-4 lg:p-8">
          <Card className="w-full max-w-md backdrop-blur-xl shadow-2xl border border-gray-800" style={{ backgroundColor: '#0D0D0D' }}>
            <CardContent className="p-10">
              {/* Logo for Mobile */}
              <div className="lg:hidden flex flex-col items-center mb-8">
                <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-24 mb-4" />
                <h2 className="text-3xl font-bold text-[#C9A961] font-serif italic">Command Center</h2>
              </div>

              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8 border border-gray-700 p-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <TabsTrigger 
                    value="login"
                    className="data-[state=active]:bg-[#C9A961] data-[state=active]:text-black text-[#C9A961] font-semibold uppercase tracking-wider text-sm"
                  >
                    Login
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register"
                    className="data-[state=active]:bg-[#C9A961] data-[state=active]:text-black text-[#C9A961] font-semibold uppercase tracking-wider text-sm"
                  >
                    Register
                  </TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-6">
                    <div className="space-y-2" style={{ background: 'transparent' }}>
                      <Label htmlFor="username" className="text-gray-300 text-sm uppercase tracking-wider block" style={{ background: 'transparent', color: '#d1d5db' }}>Username</Label>
                      <div className="relative" style={{ background: 'transparent' }}>
                        <User className="absolute left-4 top-3.5 h-5 w-5 text-gray-500" />
                        <Input
                          id="username"
                          type="text"
                          placeholder="Enter your username"
                          value={loginData.username}
                          onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                          required
                          className="pl-12 h-12 text-base text-white"
                          style={{ backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid #374151' }}
                        />
                      </div>
                    </div>

                    <div className="space-y-2" style={{ background: 'transparent' }}>
                      <Label htmlFor="password" className="text-gray-300 text-sm uppercase tracking-wider block" style={{ background: 'transparent', color: '#d1d5db' }}>Password</Label>
                      <div className="relative" style={{ background: 'transparent' }}>
                        <Lock className="absolute left-4 top-3.5 h-5 w-5 text-gray-500" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          className="pl-12 pr-12 h-12 text-base text-white"
                          style={{ backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid #374151' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-3.5 text-gray-500 hover:text-[#C9A961] transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full text-black hover:opacity-90 font-bold uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] h-12 text-base"
                      style={{ backgroundColor: '#C9A961' }}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                          Logging in...
                        </span>
                      ) : (
                        'Login'
                      )}
                    </Button>

                    <div className="relative my-8">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-700"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 text-gray-500 uppercase tracking-wider text-xs" style={{ backgroundColor: '#0D0D0D' }}>Or continue with</span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGoogleLogin}
                      className="w-full border-gray-700 bg-black/50 text-white hover:bg-gray-800 hover:text-white hover:border-gray-600 transition-all duration-300 h-12"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Sign in with Google
                    </Button>
                  </form>
                </TabsContent>

                {/* Register Tab */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name" className="text-gray-300 text-sm uppercase tracking-wider">First Name</Label>
                        <Input
                          id="first_name"
                          type="text"
                          placeholder="John"
                          value={registerData.first_name}
                          onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                          required
                          className="bg-black/50 border-gray-700 text-white focus:border-gray-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name" className="text-gray-300 text-sm uppercase tracking-wider">Last Name</Label>
                        <Input
                          id="last_name"
                          type="text"
                          placeholder="Doe"
                          value={registerData.last_name}
                          onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                          required
                          className="bg-black/50 border-gray-700 text-white focus:border-gray-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="reg_username" className="text-gray-300 text-sm uppercase tracking-wider">Username</Label>
                      <Input
                        id="reg_username"
                        type="text"
                        placeholder="Choose username"
                        value={registerData.username}
                        onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                        required
                        className="bg-black/50 border-gray-700 text-white focus:border-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300 text-sm uppercase tracking-wider">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-3 h-5 w-5 text-gray-500" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          required
                          className="pl-12 bg-black/50 border-gray-700 text-white focus:border-gray-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="reg_password" className="text-gray-300 text-sm uppercase tracking-wider">Password</Label>
                      <Input
                        id="reg_password"
                        type="password"
                        placeholder="Create password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        className="bg-black/50 border-gray-700 text-white focus:border-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="invitation_code" className="text-gray-300 text-sm uppercase tracking-wider">Invitation Code</Label>
                      <Input
                        id="invitation_code"
                        type="text"
                        placeholder="Enter invitation code"
                        value={registerData.invitation_code}
                        onChange={(e) => setRegisterData({ ...registerData, invitation_code: e.target.value })}
                        required
                        className="bg-black/50 border-gray-700 text-white focus:border-gray-500"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gold text-black hover:bg-gold-dark font-bold uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] shadow-gold h-12 mt-4"
                    >
                      {loading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
